/**
 * Function for validating fields on the fly.
 *
 * Rules can be combined using | (e.a. required|integer). Rules are passed using HTML parameter data-validate-type.
 * Some rules accept parameters. Those parameters are passed using HTML parameter data-validate-params.
 * desired format: "method1:parameter=value,parameter=value|method2|method3:parameter=value"
 * i.e. "required:min=2,max=3|integer:min=96,max=256" will return true if the value is 2 or 3 characters long and is integer between 96 and 256
 *
 * Supported rules:
 * 		required	-> checks if field is empty. Accept parameters 'min' and 'max'. If parameters are passed (or one of them) checks if value length is within the limits.
 * 		integer 	-> checks if value is integer. Accept parameters 'min' and 'max'. If parameters are passed (or one of them) checks if value is within the limits.
 * 		number 		-> checks if value is number. Accept parameters 'min' and 'max'. If parameters are passed (or one of them) checks if value is within the limits.
 * 		email 		-> checks if value looks like an e-mail
 * 		confirm 	-> checks if value is the same with some of the parameters. If parameter 'id' is set looks for field with this id and compares with its value. If parameter 'val' is set compares with it.
 * 		in 			-> checks if value is within the the values set in data-validate-params (comma separated values)
 * 		date 		-> checks if value is valid date
 * 		dateISO 	-> checks if value is in the proper ISO format (yyyy-mm-dd)
 * 		url 		-> checks if value looks like url
 * 		creditcard 	-> checks if field looks like credit card number
 */

/**
 * Messages used if global messages are not found
 *
 * @type {{}}
 */
var backupMessages = {
	"email":"The :attribute must be a valid email address",
	"required":"The :attribute field is required",
	"integer":"The :attribute must be an integer",
	"phone":"The :attribute must be a valid phone number",
	"passcheck":"The password in :attribute is incorrect",
	"confirmed":"Value for :attribute doesn't match the confirm :attribute value",
	"active_url":"The :attribute must be an active URL",
	"url":"The :attribute must be an valid URL",
	"unique":"The specified :attribute already exists ",
	"min.string":"The :attribute is too short",
	"max.string":"The :attribute is too long",
	"mimes":"The :attribute is not of the required type",
	"image-small":"The image is too small.",
	"required_without_all":"At least one :attribute must be selected",
	"numeric":"The specified :attribute must be number",
	"min.numeric":"The number in field :attribute is too small",
	"regex":"The content of field :attribute is with wrong format",
	"max.numeric":"The number in field :attribute is too big ",
	"size.string":"The :attribute must be :size characters.",
	"required_without":"The :attribute field is required when :values is not present.",
	"after":"The :attribute must be a date after :date",
	"required_with":"The :attribute field is required",
	"not_in":"The :attribute field is with duplicated value. Please select unique values for all fields.",
	"date_format":"The :attribute does not match the format :format",
	"in":"The selected :attribute is invalid",
	"date":"The :attribute must be a valid date",
	"same":"The :attribute doesn't match the required value."
};

/**
 * Validates fields against different rules.
 *
 * @param $field
 * @returns {{valid: boolean, message: string}}
 */
function validateField($field) {
	var validationDetails = getValidationDetails($field.attr('data-validate-type')),
		field_value = $field.val(),
		field_name = getFieldName($field).replace(/_|\-/g, ' '),
		to_return = {valid: true, message: ''};

	var ValidationMethods = {
		required: function(val, params) {
			var response = (val.length > 0), msg = getMessage('required');
			if (response && params.length > 0) {
				params.forEach(function(rule) {
					if(rule[0] === 'min' && val.length < parseInt(rule[1])) {
						response = false;
						msg = getMessage('min.string');
					} else if(rule[0] === 'max' && val.length > parseInt(rule[1])) {
						response = false;
						msg = getMessage('max.string');
					}
				});
			}

			return {valid: response, message: msg.replace(':attribute', field_name)};
		},

		integer: function(val, params) {
			var response =  /^\d+$/.test( val), msg = getMessage('integer');
			if (response && params.length > 0) {
				params.forEach(function(rule) {
					if(rule[0] === 'min' && parseInt(field_value) < parseInt(rule[1])) {
						response = false;
						msg = getMessage('min.numeric');
					} else if(rule[0] === 'max' && parseInt(field_value) > parseInt(rule[1])) {
						response = false;
						msg = getMessage('max.numeric');
					}
				});
			}

			return {valid: response, message: msg.replace(':attribute', field_name)};
		},

		number: function(val, params) {
			var response =  /^(?:-?\d+|-?\d{1,3}(?:,\d{3})+)?(?:\.\d+)?$/.test( val), msg = getMessage('numeric');
			if (response && params.length > 0) {
				params.forEach(function(rule) {
					if(rule[0] === 'min' && parseFloat(field_value) < parseFloat(rule[1])) {
						response = false;
						msg = getMessage('max.numeric');
					} else if(rule[0] === 'max' && parseFloat(field_value) > parseFloat(rule[1])) {
						response = false;
						msg = getMessage('min.numeric');
					}
				});
			}

			return {valid: response, message: msg.replace(':attribute', field_name)};
		},

		email: function(val) {
			//Attempt #1: /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
			//Attempt #2: /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])+\.[a-zA-Z0-9]*$/
			//Attempt #3: (be simple - check there's only one at symbol (@) and at least one dot after it)
			var response = /^[^@]+@[a-zA-Z0-9]+\.[^@]+$/.test( val );

			return {valid: response, message: getMessage('email').replace(':attribute', field_name)};
		},

		confirm: function(val, params) {
			var response = true, msg = '';
			if (params.length === 0) {
				response = false;
			} else {
				params.forEach(function(rule) {
					if(rule[0] === 'id' && field_value != $('#' + rule[1]).val()) {
						response = false;
						msg = getMessage('confirmed').replace(/:attribute/g, $('#' + rule[1]).attr('name').replace(/_|\-/g, ' '));
					} else if(rule[0] === 'val' && field_value != rule[1]) {
						response = false;
						msg = getMessage('same').replace(/:attribute/g, field_name);
					}
				});
			}

			return {valid: response, message: msg};
		},

		in: function(val, params) {
			var response = true;
			if (params.length === 0) {
				response = false;
			} else {
				response = false;
				$.each(params, function(index, value) {
					if (val == doTrim(value)) response = true;
				});
			}

			return {valid: response, message: getMessage('in').replace(':attribute', field_name)};
		},

		date: function(val) {
			var response = !/Invalid|NaN/.test( new Date( val ).toString() );

			return {valid: response, message: getMessage('date').replace(':attribute', field_name)};
		},

		dateISO: function(val) {
			var response = /^\d{4}[\/\-](0?[1-9]|1[012])[\/\-](0?[1-9]|[12][0-9]|3[01])$/.test( val );

			return {valid: response, message: getMessage('date_format').replace(':attribute', field_name).replace(':format', 'YYYY-MM-DD')};
		},

		url: function(val) {
			var response = /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})).?)(?::\d{2,5})?(?:[/?#]\S*)?$/i.test( val );

			return {valid: response, message: getMessage('url').replace(':attribute', field_name)};
		},

		creditcard: function(val) {
			var response = true;

			// accept only spaces, digits and dashes
			if ( /[^0-9 \-]+/.test( val ) ) {
				response = false;
			}

			if (response) {
				var nCheck = 0,
					nDigit = 0,
					bEven = false,
					n, cDigit;

				var value = val.replace(/\D/g, "");

				// Basing min and max length on
				// http://developer.ean.com/general_info/Valid_Credit_Card_Types
				if (value.length < 13 || value.length > 19) {
					response = false;
				}
			}

			if (response) {
				for (n = value.length - 1; n >= 0; n--) {
					cDigit = value.charAt(n);
					nDigit = parseInt(cDigit, 10);
					if (bEven) {
						if (( nDigit *= 2 ) > 9) {
							nDigit -= 9;
						}
					}
					nCheck += nDigit;
					bEven = !bEven;
				}

				response = ( nCheck % 10 ) === 0;
			}

			return {valid: response, message: getMessage('regex').replace(':attribute', field_name)};
		}
	};

	$.each(validationDetails, function(index, value) {
		if (!to_return.valid) return false;

		switch (value[0]) {
			case 'required':
				to_return = ValidationMethods.required(field_value, value[1]) ;
				break;
			case 'integer':
				to_return = ValidationMethods.integer(field_value, value[1]);
				break;
			case 'number':
				to_return = ValidationMethods.number(field_value, value[1]);
				break;
			case 'email':
				to_return = ValidationMethods.email(field_value);
				break;
			case 'url':
				to_return = ValidationMethods.url(field_value);
				break;
			case 'confirm':
				to_return = ValidationMethods.confirm(field_value, value[1]);
				break;
			case 'in':
				to_return = ValidationMethods.in(field_value, value[1]);
				break;
			case 'date':
				to_return = ValidationMethods.date(field_value);
				break;
			case 'dateISO':
				to_return = ValidationMethods.dateISO(field_value);
				break;
			case 'creditcard':
				to_return = ValidationMethods.creditcard(field_value);
				break;
			default:
				to_return = {valid: true, message: 'Unknown validation method'};
		}
	});

	return to_return;
}

window.validateField = function($field) {
	return validateField($field);
}

/**
 * Parse validation data and returns array with rules and their parameters
 *
 * @param validation_data
 * @returns {Array}
 */
function getValidationDetails(validation_data) {
	var rules = [];
	validation_data.split('|').forEach(function(rule, index){
		var tempArr = rule.split(':'), rul = doTrim(tempArr[0]), temp_params = (tempArr[1] !== undefined) ? doTrim(tempArr[1]) : '', params = [];
		temp_params = (temp_params !== '') ? temp_params.split(',') : [];

		temp_params.forEach(function(par, index){
			var temp_par = par.split('=');
			if (temp_par.length == 1) {
				params[index] = doTrim(temp_par[0]);
			} else {
				params[index] = [doTrim(temp_par[0]), doTrim(temp_par[1])];
			}
		});

		rules[index] = [rul, params];
	}) ;

	return rules;
}


/**
 * trims string. Replaces .Trim(), as it is not supported by older browsers
 *
 * @param s
 * @returns {XML|string|void|*}
 */
function doTrim(s) {
	return s.replace(/^\s+|\s+$/gm,'');
}

/**
 * Tries to retrieve field's name
 *
 * @param $field
 * @returns {string}
 */
function getFieldName($field) {
	var field_name = ($field.attr('name') === undefined) ? (($field.attr('data-stripe') === undefined) ? '' : 'card ' + $field.attr('data-stripe')) : $field.attr('name');
	if (field_name === undefined) field_name = '';
	return field_name;
}


/**
 * Looks for message in global values, if not found looks in backup messages in this file
 *
 * @param msg
 * @returns {string}
 */
function getMessage(msg) {
	var global_messages = window.messages, to_return = '';
	if (global_messages !== undefined && global_messages[msg] !== undefined) {
		to_return = global_messages[msg];
	} else if (backupMessages[msg] !== undefined) {
		to_return = backupMessages[msg];
	}

	return to_return;
}
